import { DateTime } from "luxon";

import {
  DateOnly,
  DateTimeWithZ,
  ShortDateTimeWithSpaceDelimiter,
  TimeOnlyWithZ,
} from "../types/date-time-types";

const FORMAT_ISO_DATE_SPACE_DATE_TIME = "yyyy-MM-dd hh:mm";
const FORMAT_DATE_ONLY = "yyyy-MM-dd";
const RE_TIME_WITH_Z = /\d\d:\d\dZ/;

export function isDateTime(o: any): o is DateTime {
  return DateTime.isDateTime(o);
}

function isShortDateTimeWithSpaceDelimiter(
  datetime: string | ShortDateTimeWithSpaceDelimiter
): datetime is ShortDateTimeWithSpaceDelimiter {
  return DateTime.fromFormat(datetime, FORMAT_ISO_DATE_SPACE_DATE_TIME).isValid;
}

export function ofShortDateTimeWithSpaceDelimiter(
  datetime: string
): ShortDateTimeWithSpaceDelimiter {
  if (isShortDateTimeWithSpaceDelimiter(datetime)) return datetime;
  throw new TypeError("Not a ShortDateTimeWithSpaceDelimiter:" + datetime);
}

function isDateOnly(date: string): date is DateOnly {
  return DateTime.fromFormat(date, FORMAT_DATE_ONLY).isValid;
}

export function ofDateOnly(date: string): DateOnly {
  if (isDateOnly(date)) return date;
  throw new TypeError("Not a DateOnly:" + date);
}

function isTimeOnlyWithZ(time: string): time is TimeOnlyWithZ {
  return RE_TIME_WITH_Z.test(time);
}

export function ofTimeOnlyWithZ(timeWithZ: string): TimeOnlyWithZ {
  if (isTimeOnlyWithZ(timeWithZ)) return timeWithZ;
  throw new TypeError("Not a TimeOnlyWithZ:" + timeWithZ);
}

function isDateTimeWithZ(datetime: string): datetime is DateTimeWithZ {
  if (datetime[19] !== "Z") return false;
  return DateTime.fromISO(datetime).isValid;
}

export function ofDateTimeWithZ(datetime: string): DateTimeWithZ {
  if (isDateTimeWithZ(datetime)) return datetime;
  throw new TypeError("Not a DateTimeWithZ:" + datetime);
}

// Converters
export function toShortDateTimeWithSpaceDelimiter(
  datetime: DateTimeWithZ
): ShortDateTimeWithSpaceDelimiter {
  if (!isDateTimeWithZ(datetime)) {
    throw new TypeError(
      "Not ISO8601 with Z as offset. Indicates invalid assumptions on input:" + datetime
    );
  }
  const converted = (datetime as string).substr(0, 16).replace("T", " ");
  if (!isShortDateTimeWithSpaceDelimiter(converted)) {
    throw new TypeError("Somehow we did not convert this correctly: " + converted);
  }

  return converted;
}

export function todaysDate(): DateOnly {
  return toDateOnly(DateTime.local());
}

export function nowAsDateTimeWithZ(): DateTimeWithZ {
  return toDateTimeWithZ(DateTime.local());
}

export function toDateTimeWithZ(d: DateTime | string): DateTimeWithZ {
  let asString: string;
  if (isDateTime(d)) {
    asString = d.toUTC().plus({ minutes: d.offset }).toISO();
  } else {
    asString = d;
  }
  return (asString.substr(0, 19) + "Z") as DateTimeWithZ;
}

export function toDateOnly(d: DateTime): DateOnly {
  return d.toISODate() as DateOnly;
}

function splitTimeIntoStrings(time: TimeOnlyWithZ) {
  return toLocalTime(time).split(":");
}

export function getHours(time: TimeOnlyWithZ) {
  return parseInt(splitTimeIntoStrings(time)[0]);
}

export function getMinutes(time: TimeOnlyWithZ) {
  return parseInt(splitTimeIntoStrings(time)[1]);
}

export function toLocalTime(t: TimeOnlyWithZ) {
  return t.replace("Z", "");
}

export enum FormatOptions {
  ISO_DATE_ONLY = 3,
  ISO_DATETIME_WITH_Z = 6,
  YY_MM = 9,
  ISO_DATETIME_WITH_Z_NO_SECONDS = 10,
}

export function formatDate(date: Date, format: FormatOptions): string {
  // noinspection SuspiciousTypeOfGuard
  if (typeof date === "string") {
    console.error(`Something called \`formatDate(d,opt)\` with a string as input:"${date}"`);
    // we do not accept it, but Ninestack's love of `any` forces us to handle it
    date = new Date(date);
  }
  const d = DateTime.fromJSDate(date);

  switch (format) {
    case FormatOptions.ISO_DATE_ONLY: // YY-MM-DD
      return d.toISODate();
    case FormatOptions.ISO_DATETIME_WITH_Z: // YY-MM-DDThh:min:secZ
      return d.startOf("second").toISO({ includeOffset: false, suppressMilliseconds: true }) + "Z";
    case FormatOptions.YY_MM: // YY-MM
      return d.toISODate().substr(0, 7);
    case FormatOptions.ISO_DATETIME_WITH_Z_NO_SECONDS: // YY-MM-DDThh:minZ
      return (
        d.startOf("minute").startOf("second").toISO({
          includeOffset: false,
          suppressMilliseconds: true,
          suppressSeconds: true,
        }) + "Z"
      );
    default:
      throw new TypeError("Invalid option");
  }
}
